import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Popup, Select } from '_atoms';
import { Typography } from '_atoms/Typography';
import { getListJobs } from 'actions';
import { OPENING_STATUSES } from 'constants/opening';
import { useDebounceEffect } from 'helpers/useDebounceEffect';
import type { SelectOption } from '_atoms/Select/types';
import type { JobListItem, JobListLiteItem } from 'store/reducers/jobReducer';
import './style.scss';

const JOBS_LIMIT = 25;
const FORM_NAME = 'form-select-promotion-request-job';

interface PopupSelectPromotionRequestJobProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (job: SelectOption) => void;
}

interface FormValues {
  job: SelectOption;
}

const jobOption = ({ uuid, name }: JobListItem | JobListLiteItem): SelectOption => ({
  value: uuid,
  label: name,
});

export const PopupSelectPromotionRequestJob: React.FC<PopupSelectPromotionRequestJobProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [jobOptions, setJobOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  const loadJobs = useCallback((searchValue = '') => {
    setLoading(true);
    getListJobs(
      {
        limit: JOBS_LIMIT,
        search: searchValue,
        opening_status: OPENING_STATUSES.OPEN,
      },
      'v2'
    )
      .then(resp => {
        const jobs = Array.isArray(resp) ? resp : resp?.results ?? [];
        setJobOptions(jobs.map(jobOption));
      })
      .catch(() => setJobOptions([]))
      .finally(() => setLoading(false));
  }, []);

  const loadJobsDebounced = useDebounceEffect(loadJobs, 300);

  useEffect(() => {
    if (open) loadJobs();
  }, [loadJobs, open]);

  const handleClose = () => {
    form.resetFields();
    setJobOptions([]);
    setLoading(false);
    onClose();
  };

  return (
    <Popup
      className="popup-select-promotion-request-job"
      open={open}
      title={t('translation:popup_select_promotion_request_job.title')}
      onClose={handleClose}
      onCancel={handleClose}
      cancelButtonProps={{
        onClick: handleClose,
        children: t('translation:popup_select_promotion_request_job.button_cancel'),
        variant: 'grey',
      }}
      okButtonProps={{
        form: FORM_NAME,
        htmlType: 'submit',
        children: t('translation:popup_select_promotion_request_job.button_proceed'),
      }}
    >
      <Typography.Text className="popup-select-promotion-request-job__message">
        {t('translation:popup_select_promotion_request_job.message')}
      </Typography.Text>
      <Form name={FORM_NAME} form={form} validateTrigger="onSubmit" onFinish={({ job }) => onConfirm(job)}>
        <Select
          labelInValue
          allowClear
          label={t('translation:popup_select_promotion_request_job.label_job')}
          placeholder={t('translation:popup_select_promotion_request_job.ph_select_job')}
          options={jobOptions}
          loading={loading}
          filterOption={false}
          onSearch={loadJobsDebounced}
          itemProps={{
            name: 'job',
            rules: [{ required: true, message: t('translation:validation.required') }],
          }}
          empty={<Typography.Text>{t('translation:popup_select_promotion_request_job.text_no_jobs')}</Typography.Text>}
        />
      </Form>
    </Popup>
  );
};
